import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../api/client";
import ScheduleFormModal from "./ScheduleFormModal";

interface Schedule {
  schedule_id: string;
  template_ids: string[];
  cooperatie_id: string;
  comaker_ids: string[];
  werkpakketten: string[];
  frequency: "daily" | "weekly" | "monthly";
  hour: number;
  minute: number;
  weekday: number | null;
  day_of_month: number | null;
  recipients: { email: string; name: string | null }[];
  active: boolean;
  created_at: string;
  last_run_at: string | null;
  next_run_at: string | null;
}

interface Cooperatie {
  id: string;
  naam: string;
  werksoort: string | null;
}

const WEEKDAYS = ["ma", "di", "wo", "do", "vr", "za", "zo"];

export default function SchedulesTab() {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);

  const schedules = useQuery({
    queryKey: ["schedules"],
    queryFn: async () => (await api.get<Schedule[]>("/schedules")).data,
    refetchInterval: 10_000,
  });

  const coops = useQuery({
    queryKey: ["cooperaties"],
    queryFn: async () => (await api.get<Cooperatie[]>("/cooperaties")).data,
  });

  const coopName = (id: string) => coops.data?.find((c) => c.id === id)?.naam ?? id.slice(0, 8);

  const toggleActive = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      await api.patch(`/schedules/${id}`, { active });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedules"] }),
  });

  const deleteSchedule = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/schedules/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["schedules"] }),
  });

  const handleDelete = (s: Schedule) => {
    if (confirm(`Schedule voor ${coopName(s.cooperatie_id)} verwijderen?`)) {
      deleteSchedule.mutate(s.schedule_id);
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-kbs-primary">Geplande rapporten</h2>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="rounded bg-kbs-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-kbs-dark"
        >
          + Nieuwe schedule
        </button>
      </div>

      {schedules.isLoading && <p className="mt-3 text-sm text-slate-500">Laden…</p>}
      {schedules.error && (
        <p className="mt-3 text-sm text-rose-600">
          Fout: {(schedules.error as Error).message}
        </p>
      )}
      {schedules.data && schedules.data.length === 0 && (
        <p className="mt-3 text-sm text-slate-500">Nog geen geplande rapporten ingesteld.</p>
      )}
      {schedules.data && schedules.data.length > 0 && (
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 font-medium">Coöperatie</th>
              <th className="py-2 font-medium">Template</th>
              <th className="py-2 font-medium">Frequentie</th>
              <th className="py-2 font-medium">Volgende run</th>
              <th className="py-2 font-medium">Ontvangers</th>
              <th className="py-2 font-medium">Status</th>
              <th className="py-2 font-medium">Actie</th>
            </tr>
          </thead>
          <tbody>
            {schedules.data.map((s) => (
              <tr key={s.schedule_id} className="border-b border-slate-100">
                <td className="py-2">{coopName(s.cooperatie_id)}</td>
                <td className="py-2 text-slate-600" title={s.template_ids.join(", ")}>
                  {s.template_ids.length === 1
                    ? s.template_ids[0]
                    : `${s.template_ids.length} templates`}
                </td>
                <td className="py-2">{describeSchedule(s)}</td>
                <td className="py-2 text-slate-600">
                  {s.next_run_at ? new Date(s.next_run_at).toLocaleString("nl-NL") : "—"}
                </td>
                <td
                  className="py-2 text-slate-600"
                  title={s.recipients
                    .map((r) => (r.name ? `${r.name} <${r.email}>` : r.email))
                    .join(", ")}
                >
                  {s.recipients.length === 1
                    ? (s.recipients[0].name ?? s.recipients[0].email)
                    : `${s.recipients.length} ontvangers`}
                </td>
                <td className="py-2">
                  <span
                    className={
                      "rounded px-2 py-0.5 text-xs font-medium " +
                      (s.active
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-slate-100 text-slate-500")
                    }
                  >
                    {s.active ? "Actief" : "Gepauzeerd"}
                  </span>
                </td>
                <td className="py-2">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        toggleActive.mutate({ id: s.schedule_id, active: !s.active })
                      }
                      disabled={toggleActive.isPending}
                      className="text-kbs-accent hover:underline disabled:opacity-40"
                    >
                      {s.active ? "Pauzeer" : "Activeer"}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(s)}
                      disabled={deleteSchedule.isPending}
                      className="text-rose-600 hover:underline disabled:opacity-40"
                    >
                      Verwijder
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <ScheduleFormModal
          onClose={() => setShowModal(false)}
          onCreated={() => {
            setShowModal(false);
            qc.invalidateQueries({ queryKey: ["schedules"] });
          }}
        />
      )}
    </section>
  );
}

function describeSchedule(s: Schedule): string {
  const time = `${String(s.hour).padStart(2, "0")}:${String(s.minute).padStart(2, "0")}`;
  if (s.frequency === "daily") return `Dagelijks om ${time}`;
  if (s.frequency === "weekly") {
    const wd = s.weekday !== null ? WEEKDAYS[s.weekday] : "?";
    return `Wekelijks (${wd}) om ${time}`;
  }
  if (s.frequency === "monthly") return `Maandelijks op dag ${s.day_of_month} om ${time}`;
  return s.frequency;
}
